import { doc, deleteDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import type { Messaging } from 'firebase/messaging';
import { getDb, getFirebaseAppClient } from '@talk2nick/shared';
import { recoverToken } from './recoverToken';

/**
 * FCM 푸시 알림 등록 (PLAN 5-8-8장).
 *
 * 저장 구조: `pushTokens/{uid}/tokens/{token}` — token을 문서 ID로 쓰는 이유:
 * - 같은 토큰 재등록이 setDoc merge로 멱등하다.
 * - pushTokens/{uid}는 짝수 세그먼트라 Firestore에서 컬렉션 경로가 될 수 없으므로
 *   tokens 서브컬렉션을 둔다 (Rules·Functions·housekeeping이 같은 구조를 쓴다).
 *
 * 권한은 OS(Notification.permission)와 서버 측(privacy.notificationsOff) 두 층으로
 * 나뉜다. 서버가 발송 대상에서 제외하므로 토큰을 지울 필요가 없고, 토글을 다시 켜면
 * 기존 토큰이 그대로 살아난다.
 */

export type PushRegistrationState =
  | 'unsupported' // Service Worker / Push API 없는 환경
  | 'no-vapid-key' // VITE_FIREBASE_VAPID_KEY 미설정 (배포 설정 누락)
  | 'denied' // 사용자가 권한을 거부함
  | 'idle' // 미등록 (켤 수 있음)
  | 'registering'
  | 'active'
  | 'error';

export interface PushRegistration {
  state: PushRegistrationState;
  /** 활성 등록 토큰 — 디버그/문의용 */
  token?: string;
  /** 실패 상세 메시지 또는 에러 코드 (디버깅/사용자 안내용) */
  errorMessage?: string;
}

let cachedRegistration: PushRegistration | null = null;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 프라미스가 `ms` 안에 풀리지 않으면 명시적 에러로 거절한다.
 *
 * FCM/iOS WebKit의 일부 API(requestPermission, pushManager.subscribe,
 * Firestore 오프라인 setDoc 등)는 무응답 상태에 빠질 수 있다. 경계가 없으면
 * UI가 'registering'(켜는 중)에 영원히 갇힌다 (제보: 앱 업데이트 직후 첫 실행).
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timeout after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Notification.requestPermission() — iOS WebKit에는 OS가 권한을 실제로
 * 바꿔치워도 프라미스가 풀리지 않는 경우가 있다(홈 화면 앱 업데이트 직후
 * 첫 요청 등). 그래서 프라미스와 OS의 실제 permission 값을 500ms 간격으로
 * 함께 확인해 먼저 확정된 쪽을 따른다. 30초가 지나도 default면 포기하고
 * 'default'를 돌려주어 호출부가 'idle'로 복귀 — 사용자가 다시 눌러 재시도할 수 있다.
 */
async function requestPermissionSafely(): Promise<NotificationPermission> {
  const asked = Notification.requestPermission().then(
    (value) => ({ value }),
    () => ({ value: null as NotificationPermission | null }),
  );
  const deadline = Date.now() + 30_000;
  for (;;) {
    const native = Notification.permission;
    if (native !== 'default') return native;
    const result = await Promise.race([asked, delay(500).then(() => null)]);
    if (result !== null) return result.value ?? 'default';
    if (Date.now() >= deadline) {
      console.warn('[talk2nIcK] Notification.requestPermission() 무응답 — 재시도에 맡긴다');
      return 'default';
    }
  }
}

async function getRegistrationToken(
  messaging: Messaging,
  vapidKey: string,
  worker: ServiceWorkerRegistration | null,
): Promise<string> {
  const { getToken } = await withTimeout(
    import('firebase/messaging'),
    8_000,
    'firebase/messaging import',
  );
  // pushManager.subscribe()도 iOS에서 무응답할 수 있으므로 토큰 발급 전체에 경계를 둔다
  return withTimeout(
    recoverToken(
      () =>
        getToken(messaging, {
          vapidKey,
          ...(worker ? { serviceWorkerRegistration: worker } : {}),
        }),
      async () => {
        const { getInstallations, getToken: refreshToken } = await import('firebase/installations');
        await refreshToken(getInstallations(messaging.app), true);
      },
    ),
    20_000,
    'FCM token issuance',
  );
}

/** 현재 등록 상태 (동기 조회 — 캐시 없으면 판정만 수행) */
export function getPushRegistration(): PushRegistration {
  if (cachedRegistration) return cachedRegistration;
  return { state: detectUnsupportedState() };
}

function detectUnsupportedState(): PushRegistrationState {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return 'unsupported';
  return 'idle';
}

async function getReadyWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    // 1. 이미 등록되어 활성화된 워커가 있는지 확인
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg?.active) return reg;

    // 2. navigator.serviceWorker.ready 대기 (최대 3초)
    const readyPromise = navigator.serviceWorker.ready;
    const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000));
    const readyReg = await Promise.race([readyPromise, timeoutPromise]);
    if (readyReg) return readyReg;

    // 3. 없으면 직접 sw.js 등록 시도 (update 직후 register가 무응답할 수 있으므로 경계)
    return await withTimeout(
      navigator.serviceWorker.register('/sw.js', { scope: '/' }),
      4_000,
      'serviceWorker.register',
    );
  } catch (err) {
    console.warn('[talk2nIcK] ServiceWorker registration retrieval fallback:', err);
    return null;
  }
}

/**
 * 푸시 알림 켜기. Notification 권한 요청 → 토큰 발급 → Firestore 저장까지 수행.
 * 이미 권한이 있으면 프롬프트 없이 등록만 갱신한다.
 */
export async function enablePush(myUid: string): Promise<PushRegistration> {
  cachedRegistration = { state: 'registering' };

  try {
    if (detectUnsupportedState() === 'unsupported') {
      cachedRegistration = { state: 'unsupported' };
      return cachedRegistration;
    }

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
    if (!vapidKey) {
      cachedRegistration = { state: 'no-vapid-key' };
      return cachedRegistration;
    }

    // 1. Android/iOS Safari 등 브라우저 제스처 유효 시간 내에 Notification 권한 요청을 최우선 실행
    let permission: NotificationPermission = Notification.permission;
    if (permission === 'default') {
      permission = await requestPermissionSafely();
    }

    if (permission === 'denied') {
      cachedRegistration = { state: 'denied' };
      return cachedRegistration;
    }

    if (permission !== 'granted') {
      // 프롬프트를 닫았거나 허용하지 않고 넘긴 경우 (requestPermission 무응답으로
      // 포기한 경우도 여기로 와서 'idle' — 버튼이 풀리고 재시도할 수 있다)
      cachedRegistration = { state: 'idle' };
      return cachedRegistration;
    }

    // 2. Firebase Messaging 모듈 및 서비스워커 준비
    const { getMessaging, isSupported } = await withTimeout(
      import('firebase/messaging'),
      8_000,
      'firebase/messaging import',
    );

    if (!(await withTimeout(isSupported(), 5_000, 'messaging isSupported'))) {
      cachedRegistration = { state: 'unsupported' };
      return cachedRegistration;
    }

    const worker = await getReadyWorker();
    const app = getFirebaseAppClient();
    if (!app) {
      cachedRegistration = { state: 'error', errorMessage: 'Firebase App not initialized' };
      return cachedRegistration;
    }

    const messaging = getMessaging(app);
    const token = await getRegistrationToken(messaging, vapidKey, worker);

    if (!token) {
      cachedRegistration = { state: 'error', errorMessage: 'No FCM token returned' };
      return cachedRegistration;
    }

    // Firestore setDoc은 서버 ack를 기다리므로 오프라인/지연 시 무한히 걸릴 수 있다
    await withTimeout(saveToken(myUid, token), 15_000, 'token save');
    void listenForeground(messaging);

    cachedRegistration = { state: 'active', token };
    return cachedRegistration;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('[talk2nIcK] 푸시 등록 실패:', error);
    cachedRegistration = { state: 'error', errorMessage: errorMsg };
    return cachedRegistration;
  }
}

/**
 * 푸시 알림 끄기. OS 권한은 회수할 수 없으므로(브라우저 제약) 서버 측 스위치인
 * `privacy.notificationsOff`를 켜 Functions가 발송 대상에서 제외하게 한다.
 * 토큰은 남긴다 — 다시 켜면 권한 프롬프트 없이 즉시 복귀한다.
 */
export async function disablePush(myUid: string): Promise<void> {
  await setDoc(doc(getDb(), 'privacy', myUid), { notificationsOff: true }, { merge: true });
}

const LOCAL_TOKEN_STORAGE_KEY = 'talk2nick.fcm_token';

/** 토큰 저장 (멱등 — 같은 토큰 재등록은 lastSeenAt만 갱신, 다기기 분기용 메타데이터 기록) */
async function saveToken(myUid: string, token: string): Promise<void> {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';

  try {
    window.localStorage.setItem(LOCAL_TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage 접근 불가 시 무시
  }

  await setDoc(
    doc(getDb(), 'pushTokens', myUid, 'tokens', token),
    {
      lastSeenAt: serverTimestamp(),
      platform,
      userAgent: ua.slice(0, 200),
    },
    { merge: true },
  );
}

/** 로그아웃/기기 초기화용 — 현재 기기 토큰 삭제 (동일 기기 계정 전환 시 이전 계정 알림 수신 방지) */
export async function removeCurrentToken(myUid: string): Promise<void> {
  let token = cachedRegistration?.token;
  if (!token && typeof window !== 'undefined') {
    try {
      token = window.localStorage.getItem(LOCAL_TOKEN_STORAGE_KEY) ?? undefined;
    } catch {
      // ignore
    }
  }
  if (!token) {
    try {
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
      const app = getFirebaseAppClient();
      const worker = await getReadyWorker();
      if (vapidKey && app && worker && Notification.permission === 'granted') {
        const { getMessaging, getToken } = await import('firebase/messaging');
        token = await withTimeout(
          getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration: worker }),
          4_000,
          'logout getToken',
        );
      }
    } catch {
      // ignore token lookup failure on logout
    }
  }
  if (token) {
    await deleteDoc(doc(getDb(), 'pushTokens', myUid, 'tokens', token)).catch(() => undefined);
  }
  cachedRegistration = { state: 'idle' };
}

/**
 * 포그라운드 수신 — OS 알림은 NotificationHost(인앱 배너)가 담당하므로
 * 여기서는 등록만 해 두고 표시하지 않는다. 리스너가 없으면 FCM이
 * 콘솔 경고를 남기므로 no-op 구독으로 조용히 한다.
 */
let foregroundBound = false;
async function listenForeground(messaging: Messaging): Promise<void> {
  if (foregroundBound) return;
  foregroundBound = true;
  const { onMessage } = await import('firebase/messaging');
  onMessage(messaging, () => {
    // 포그라운드 배너는 NotificationHost가 Firestore 리스너로 처리한다 (PLAN 5-8-4장)
  });
}

/**
 * 등록 상태 구독 — 설정 화면이 이 값으로 토글을 그린다.
 * Firestore 토큰 문서의 존재 여부까지 확인해 다른 기기 등록과 섞이지 않게 한다.
 */
export function observePushRegistration(
  myUid: string,
  onUpdate: (state: PushRegistration) => void,
): () => void {
  let unsubDoc: (() => void) | null = null;

  void (async () => {
    if (detectUnsupportedState() === 'unsupported') {
      cachedRegistration = { state: 'unsupported' };
      onUpdate(cachedRegistration);
      return;
    }
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
    if (!vapidKey) {
      cachedRegistration = { state: 'no-vapid-key' };
      onUpdate(cachedRegistration);
      return;
    }

    try {
      if (typeof Notification === 'undefined') {
        cachedRegistration = { state: 'unsupported' };
        onUpdate(cachedRegistration);
        return;
      }
      if (Notification.permission === 'denied') {
        cachedRegistration = { state: 'denied' };
        onUpdate(cachedRegistration);
        return;
      }
      if (Notification.permission !== 'granted') {
        // 권한 요청 전 상태('default') — FCM getToken()을 호출하면 permission error를 뱉으므로
        // idle 상태로 대기하고 사용자가 설정에서 토글을 켰을 때 비로소 요청한다.
        cachedRegistration = { state: 'idle' };
        onUpdate(cachedRegistration);
        return;
      }

      const worker = await getReadyWorker();
      const app = getFirebaseAppClient();
      if (!worker || !app) {
        cachedRegistration = { state: 'idle' };
        onUpdate(cachedRegistration);
        return;
      }
      const { getMessaging } = await withTimeout(
        import('firebase/messaging'),
        8_000,
        'firebase/messaging import',
      );
      const token = await getRegistrationToken(getMessaging(app), vapidKey, worker).catch((err) => {
        console.warn('[talk2nIcK] 백그라운드 토큰 조회 보류:', err);
        return null;
      });

      if (!token) {
        cachedRegistration = { state: 'idle' };
        onUpdate(cachedRegistration);
        return;
      }

      // 토큰 문서 구독 — 문서가 있으면 이 기기 등록이 살아 있는 것
      unsubDoc = onSnapshot(
        doc(getDb(), 'pushTokens', myUid, 'tokens', token),
        (snap) => {
          cachedRegistration = snap.exists() ? { state: 'active', token } : { state: 'idle' };
          onUpdate(cachedRegistration);
        },
        (error: unknown) => {
          console.warn('[talk2nIcK] 푸시 등록 상태 구독 보류:', error);
          cachedRegistration = { state: 'idle' };
          onUpdate(cachedRegistration);
        },
      );
    } catch (error) {
      console.warn('[talk2nIcK] 푸시 등록 상태 확인 대기:', error);
      cachedRegistration = { state: 'idle' };
      onUpdate(cachedRegistration);
    }
  })();

  return () => {
    unsubDoc?.();
  };
}
